import { NoUsers } from '@app/modules/admin/components/NoUsers';
import {
  canUserTogglePermission,
  getGroupFromPermission,
} from '@app/modules/admin/screens/stations/helpers';
import {
  AdminIndicator,
  PermissionInheritedIndicator,
  PermissionLinks,
  PermissionUserInfo,
} from '@app/modules/admin/screens/stations/users-permissions/components';
import { UserPermissionState } from '@app/modules/admin/types';
import { mapPromises, User, UserRole } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { AsyncView, NullState } from '@mi-front/ui';
import { useState } from 'react';
import { Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { reportError } from '../../../../../core/integrations/bugsnag';
import { useStation } from '../../../../../core/stations/hooks/useStation';
import { fetchUsersPermissions, updateUserPermission } from '../../../api/permissions';
import { PermissionsViewP } from '../../../components/permission/view/PermissionsViewP';

interface Props {
  readonly data: {
    readonly permissionsState: readonly UserPermissionState[];
  };
  readonly updatePermission: (data: { userId: User['id']; allowed: boolean }) => Promise<void>;
}

export function AdminStationsUsersPermissionsView(p: Props) {
  const { data, updatePermission } = p;
  const { permissionsState } = data;
  const { $t } = useIntl();
  const { notifyError } = useToast();
  const [filter, setFilter] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState<Set<User['id']>>(new Set());

  if (!permissionsState.length) {
    return <NoUsers />;
  }

  return (
    <PermissionsViewP<UserPermissionState>
      permissionsState={permissionsState}
      filter={filter}
      filterPlaceholder={$t({
        id: 'admin.stations.viewScreen.usersFilter',
        defaultMessage: 'Filtrar usuarios',
      })}
      emptyListMsg={
        <NullState>
          <FormattedMessage
            id="admin.stations.viewScreen.noUsersFound"
            defaultMessage="No se encontraron usuarios"
          />
        </NullState>
      }
      canModify={(pState) => canUserTogglePermission(pState)}
      isVisible={(pState: UserPermissionState) => {
        if (!filter) {
          return true;
        }

        const regex = new RegExp(filter, 'i');
        const { user } = pState;
        return !!(
          user.name?.match(regex) ||
          user.surname?.match(regex) ||
          user.email?.match(regex)
        );
      }}
      isUpdating={(pState) => updatingUsers.has(pState.user.id)}
      renderInfo={(pState: UserPermissionState) => {
        const group = getGroupFromPermission(pState.permission);

        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <PermissionUserInfo user={pState.user} />
            </Grid>
            <Grid item xs={12} md={5}>
              {group && <PermissionInheritedIndicator group={group} />}
              {pState.user.role === UserRole.CLIENT_ADMIN && <AdminIndicator />}
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
              <PermissionLinks user={pState.user} />
            </Grid>
          </Grid>
        );
      }}
      events={{
        setFilter,
        onToggleEnabled: async (pState: UserPermissionState, enabled: boolean) => {
          setUpdatingUsers((prev) => new Set(prev).add(pState.user.id));
          try {
            await updatePermission({
              userId: pState.user.id,
              allowed: enabled,
            });
          } catch (err) {
            reportError(err as Error);

            notifyError(
              $t({
                id: 'admin.stations.viewScreen.permissionUpdateError',
                defaultMessage: 'No se pudo actualizar el permiso',
              }),
            );
            throw err;
          } finally {
            setUpdatingUsers((prev) => {
              const next = new Set(prev);
              next.delete(pState.user.id);
              return next;
            });
          }
        },
      }}
    />
  );
}

export const ConnAdminStationsUsersPermissionsView = (p: { slug: string }) => {
  const station = useStation(p.slug);

  return (
    <AsyncView
      promiseFn={() =>
        mapPromises({
          permissionsState: fetchUsersPermissions(station.id),
        })
      }
    >
      {(pp) => (
        <AdminStationsUsersPermissionsView
          {...pp}
          updatePermission={async (update) => {
            try {
              await updateUserPermission(station.id, update);
            } catch (err) {
              reportError(err as Error);
              throw err;
            }
          }}
        />
      )}
    </AsyncView>
  );
};
